/**
 * Travel ball team announcements.
 *
 * - **Coach calls** (`/api/travel-ball/teams/{teamId}/announcements`, `/api/travel-ball/announcements/{id}`)
 *   answer 404 to anyone who is not a coach of that team, an unassigned org admin
 *   included, and to everyone while the `travelball.announcements` flag is off. The team page reads
 *   that 404 as "show nothing". A coach of the team also grants and revokes **announcers**.
 * - **Family calls** live under `/api/travel-ball/family`, the only travel ball routes a family view
 *   may call. Reading one announcement marks it read; loading the list never does. An announcer
 *   posts, edits and deletes their own posts through the family routes too.
 *
 * Refusals are thrown as an `AnnouncementError` whose `message` is always displayable.
 */

import { ApiError, del, get, patch, post, put } from './api';
import type {
  AnnounceableTeam,
  AnnouncementCoachTeam,
  AnnouncementListStatus,
  CoachAnnouncement,
  CoachAnnouncementPage,
  CreateAnnouncementRequest,
  FamilyAnnouncement,
  FamilyAnnouncementPage,
  UpdateAnnouncementRequest,
} from '../types/travelBallAnnouncements';

export const ANNOUNCEMENT_PAGE_SIZE = 20;

export const RATE_LIMITED_MESSAGE =
  "You've made a lot of announcement changes in the last hour. Wait a while and try again.";
export const NETWORK_MESSAGE = 'We could not reach RosterPilot. Check your connection and try again.';
export const UNKNOWN_MESSAGE = 'Something went wrong on our end. Please try again.';
export const NOT_FOUND_MESSAGE = 'That announcement is no longer available.';

/**
 * A refused announcement call. `code` is the server's own when the body carried one, else
 * `rate_limited`, `not_found`, `unauthorized`, `validation`, `network` or `unknown`.
 */
export class AnnouncementError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'AnnouncementError';
    this.code = code;
    this.status = status;
  }
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

/** The first message of an ASP.NET ValidationProblem (`{ errors: { Title: ["..."] } }`). */
const firstValidationMessage = (errors: unknown): string | null => {
  for (const messages of Object.values(asRecord(errors))) {
    const first = asArray<unknown>(messages).map(text).find(Boolean);
    if (first) return first;
  }
  return null;
};

const errorFromBody = (status: number, body: unknown): AnnouncementError => {
  const record = asRecord(body);
  const code = text(record.code);
  const message = text(record.message) ?? firstValidationMessage(record.errors);
  if (status === 429) return new AnnouncementError(code ?? 'rate_limited', message ?? RATE_LIMITED_MESSAGE, status);
  // Ahead of the body's own code: a 401 always signs a family out, whatever code it carries (as on mobile).
  if (status === 401) {
    return new AnnouncementError('unauthorized', message ?? 'Your session has expired. Sign in again to continue.', status);
  }
  if (code) return new AnnouncementError(code, message ?? UNKNOWN_MESSAGE, status);
  if (status === 404) return new AnnouncementError('not_found', message ?? NOT_FOUND_MESSAGE, status);
  if (status === 400) return new AnnouncementError('validation', message ?? UNKNOWN_MESSAGE, status);
  return new AnnouncementError('unknown', message ?? UNKNOWN_MESSAGE, status);
};

export const toAnnouncementError = (error: unknown): AnnouncementError => {
  if (error instanceof AnnouncementError) return error;
  if (error instanceof ApiError) {
    if (error.statusCode === 0) return new AnnouncementError('network', NETWORK_MESSAGE, 0);
    return errorFromBody(error.statusCode, error.response);
  }
  return new AnnouncementError('unknown', UNKNOWN_MESSAGE, 0);
};

const call = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (error) {
    throw toAnnouncementError(error);
  }
};

const teamPath = (teamId: string) => `/api/travel-ball/teams/${encodeURIComponent(teamId)}`;
const announcementPath = (id: string) => `/api/travel-ball/announcements/${encodeURIComponent(id)}`;
const FAMILY_PATH = '/api/travel-ball/family/announcements';
const familyTeamPath = (teamId: string) => `/api/travel-ball/family/teams/${encodeURIComponent(teamId)}`;
const familyAnnouncementPath = (id: string) => `${FAMILY_PATH}/${encodeURIComponent(id)}`;

/** Missing arrays read as empty, so a partial answer renders rather than crashes. */
export const normalizeFamilyAnnouncement = (item: FamilyAnnouncement): FamilyAnnouncement => ({
  ...item,
  playerFirstNames: asArray(item.playerFirstNames),
  // Only an explicit true offers Edit or Delete; an older answer leaves them off.
  canEdit: item.canEdit === true,
  canDelete: item.canDelete === true,
});

export const UNKNOWN_AUTHOR = 'Unknown';

/**
 * A coach's view of one post, read off the wire. A row with no id is dropped (null), missing text
 * reads as empty, a missing author reads as `UNKNOWN_AUTHOR`, and only an explicit true offers Edit
 * or Delete, so a partial or malformed answer renders the rest rather than throws mid-render.
 */
export const normalizeCoachAnnouncement = (raw: unknown): CoachAnnouncement | null => {
  const record = asRecord(raw);
  const id = text(record.id);
  if (!id) return null;
  const author = asRecord(record.author);
  const str = (value: unknown) => (typeof value === 'string' ? value : '');
  return {
    id,
    teamId: str(record.teamId),
    title: str(record.title),
    body: str(record.body),
    expiresAt: text(record.expiresAt),
    isExpired: record.isExpired === true,
    createdAt: str(record.createdAt),
    editedAt: text(record.editedAt),
    author: {
      userId: str(author.userId),
      name: text(author.name) ?? UNKNOWN_AUTHOR,
      isAnnouncer: author.isAnnouncer === true,
    },
    canEdit: record.canEdit === true,
    canDelete: record.canDelete === true,
  };
};

/** A create or edit answered with something that is not an announcement: shown as the generic failure. */
const requireCoachAnnouncement = (raw: unknown): CoachAnnouncement => {
  const announcement = normalizeCoachAnnouncement(raw);
  if (!announcement) throw new AnnouncementError('unknown', UNKNOWN_MESSAGE, 0);
  return announcement;
};

// ────────────────────────────────────────────────────────────────────────────
// Coach

export interface ListTeamAnnouncementsOptions {
  status?: AnnouncementListStatus;
  page?: number;
  pageSize?: number;
}

/** A team's announcements, newest first. 404 when the caller is not a coach of the team. */
export const listTeamAnnouncements = (
  teamId: string,
  { status = 'active', page = 1, pageSize = ANNOUNCEMENT_PAGE_SIZE }: ListTeamAnnouncementsOptions = {},
): Promise<CoachAnnouncementPage> =>
  call(async () => {
    const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
    const body = await get<{ items?: unknown; page?: number; pageSize?: number; totalCount?: number } | null>(
      `${teamPath(teamId)}/announcements?${params.toString()}`,
    );
    return {
      items: asArray<unknown>(body?.items).flatMap((item) => normalizeCoachAnnouncement(item) ?? []),
      page: body?.page ?? page,
      pageSize: body?.pageSize ?? pageSize,
      totalCount: body?.totalCount ?? 0,
    };
  });

/**
 * The teams the caller coaches and can post on: what the coach's Announcements page picks from, and
 * whether its nav item shows at all. Answers 404 while announcements are off (and to an impersonating
 * system admin), read here as no teams. Rows without an id are dropped.
 */
export const getAnnouncementTeams = (): Promise<AnnouncementCoachTeam[]> =>
  call(async () => {
    let body: { teams?: unknown } | null;
    try {
      body = await get<{ teams?: unknown }>('/api/travel-ball/announcements/teams');
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) return [];
      throw error;
    }
    return asArray<unknown>(body?.teams)
      .map(asRecord)
      .filter((t) => text(t.teamId) !== null)
      .map((t) => ({
        teamId: text(t.teamId) as string,
        teamName: text(t.teamName) ?? 'Unnamed team',
        organizationName: text(t.organizationName),
        seasonName: text(t.seasonName),
      }));
  });

export const createTeamAnnouncement = (
  teamId: string,
  request: CreateAnnouncementRequest,
): Promise<CoachAnnouncement> =>
  call(async () => requireCoachAnnouncement(await post<unknown>(`${teamPath(teamId)}/announcements`, request)));

/** Title, body and expiry only; it does not notify anyone again. */
export const updateAnnouncement = (id: string, request: UpdateAnnouncementRequest): Promise<CoachAnnouncement> =>
  call(async () => requireCoachAnnouncement(await patch<unknown>(announcementPath(id), request)));

export const deleteAnnouncement = (id: string): Promise<void> =>
  call(async () => {
    await del<void>(announcementPath(id));
  });

const announcerPath = (teamId: string, guardianUserId: string) =>
  `${teamPath(teamId)}/announcers/${encodeURIComponent(guardianUserId)}`;

/**
 * Lets a parent/guardian post announcements to this team ("Can post announcements"). Idempotent. 404
 * unless the caller is a coach of the team; 400 `not_team_guardian` unless they are an accepted
 * parent/guardian of a player on it.
 */
export const grantTeamAnnouncer = (teamId: string, guardianUserId: string): Promise<void> =>
  call(async () => {
    await put<void>(announcerPath(teamId, guardianUserId), {});
  });

/** Takes it back. Their past posts stay. Idempotent. */
export const revokeTeamAnnouncer = (teamId: string, guardianUserId: string): Promise<void> =>
  call(async () => {
    await del<void>(announcerPath(teamId, guardianUserId));
  });

// ────────────────────────────────────────────────────────────────────────────
// Family

export interface ListFamilyAnnouncementsOptions {
  status?: AnnouncementListStatus;
  /** A guardian's linked player to filter to; 404 when not linked. */
  playerUserId?: string | null;
  page?: number;
  pageSize?: number;
}

/** The reader's announcements across their teams, newest first, with the unread count. Never marks read. */
export const listFamilyAnnouncements = ({
  status = 'active',
  playerUserId,
  page = 1,
  pageSize = ANNOUNCEMENT_PAGE_SIZE,
}: ListFamilyAnnouncementsOptions = {}): Promise<FamilyAnnouncementPage> =>
  call(async () => {
    const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
    if (playerUserId) params.set('playerUserId', playerUserId);
    const body = await get<FamilyAnnouncementPage>(`${FAMILY_PATH}?${params.toString()}`);
    return {
      items: asArray<FamilyAnnouncement>(body?.items).map(normalizeFamilyAnnouncement),
      page: body?.page ?? page,
      pageSize: body?.pageSize ?? pageSize,
      totalCount: body?.totalCount ?? 0,
      unreadCount: body?.unreadCount ?? 0,
    };
  });

/** One announcement. Reading it marks it read for the caller. 404 when it is not visible to them today. */
export const getFamilyAnnouncement = (id: string): Promise<FamilyAnnouncement> =>
  call(async () =>
    normalizeFamilyAnnouncement(await get<FamilyAnnouncement>(familyAnnouncementPath(id))),
  );

/** Marks every visible announcement read, or only those reaching one linked player. */
export const markAllFamilyAnnouncementsRead = (playerUserId?: string | null): Promise<void> =>
  call(async () => {
    await post<void>(`${FAMILY_PATH}/read-all`, playerUserId ? { playerUserId } : {});
  });

/** `GET .../announcements/unread-count`: the rail badge, and whether announcements are on for the reader. */
export interface FamilyAnnouncementUnread {
  count: number;
  /** False while the `travelball.announcements` flag is off (then `count` is 0). */
  enabled: boolean;
}

/**
 * Unread active announcements across all the reader's teams, and whether the feature is on at all
 * (family views cannot read the college feature flags, so this answer is how they know). Anything but
 * an explicit `enabled: true` reads as off, a 404 included, so a missing field hides the feature
 * rather than shows it. Other failures throw, so the badge store keeps its last answer.
 */
export const getFamilyAnnouncementUnread = (): Promise<FamilyAnnouncementUnread> =>
  call(async () => {
    let body: { count?: unknown; enabled?: unknown } | null;
    try {
      body = await get<{ count?: unknown; enabled?: unknown }>(`${FAMILY_PATH}/unread-count`);
    } catch (error) {
      // An API without the endpoint (or the family routes dark): announcements are off.
      if (error instanceof ApiError && error.statusCode === 404) return { count: 0, enabled: false };
      throw error;
    }
    const enabled = body?.enabled === true;
    const count = enabled && typeof body?.count === 'number' && body.count > 0 ? body.count : 0;
    return { count, enabled };
  });

// ────────────────────────────────────────────────────────────────────────────
// Family announcers

/** Teams the reader can post to as an announcer right now. Empty while announcements are off. */
export const listAnnounceableTeams = (): Promise<AnnounceableTeam[]> =>
  call(async () =>
    asArray<AnnounceableTeam>(await get<AnnounceableTeam[]>(`${FAMILY_PATH}/announceable-teams`)).filter(
      (t) => !!t?.teamId,
    ),
  );

/** An announcer posts to a team (always the whole team). Same body and rules as a coach's post. */
export const createFamilyAnnouncement = (
  teamId: string,
  request: CreateAnnouncementRequest,
): Promise<FamilyAnnouncement> =>
  call(async () =>
    normalizeFamilyAnnouncement(await post<FamilyAnnouncement>(`${familyTeamPath(teamId)}/announcements`, request)),
  );

/** The author edits their own post: title, body and expiry only. */
export const updateFamilyAnnouncement = (id: string, request: UpdateAnnouncementRequest): Promise<FamilyAnnouncement> =>
  call(async () => normalizeFamilyAnnouncement(await patch<FamilyAnnouncement>(familyAnnouncementPath(id), request)));

/** The author deletes their own post. */
export const deleteFamilyAnnouncement = (id: string): Promise<void> =>
  call(async () => {
    await del<void>(familyAnnouncementPath(id));
  });
